import { projectsId, type ProjectsId } from "@papier/projects-schema/db_gen/public/Projects";
import {
  environmentsId,
  type EnvironmentsId,
} from "@papier/environments-schema/db_gen/public/Environments";
import {
  templateType,
  type default as TemplateType,
} from "@papier/templates-schema/db_gen/public/TemplateType";
import { notificationStatus, type default as NotificationStatus } from "./NotificationStatus";
import {
  organizationsId,
  type OrganizationsId,
} from "@papier/auth-schema/db_gen/public/Organizations";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for log_aggregations */
export type LogAggregationsId = string & { __flavor: "LogAggregationsId" };

/**
 * Represents the table public.log_aggregations
 * @uniqueConstraints {"log_aggregations_date_idx":{"name":"log_aggregations_date_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"date","definition":"date","predicate":null}]},"log_aggregations_environment_id_idx":{"name":"log_aggregations_environment_id_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"environment_id","definition":"environment_id","predicate":null}]},"log_aggregations_project_id_idx":{"name":"log_aggregations_project_id_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"project_id","definition":"project_id","predicate":null}]},"log_aggregations_recipient_type_idx":{"name":"log_aggregations_recipient_type_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"recipient_type","definition":"recipient_type","predicate":null}]},"log_aggregations_status_idx":{"name":"log_aggregations_status_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"status","definition":"status","predicate":null}]},"log_aggregations_unique_idx":{"name":"log_aggregations_unique_idx","isPrimary":false,"isUnique":true,"columns":[{"name":"date","definition":"date","predicate":null},{"name":"environment_id","definition":"environment_id","predicate":null},{"name":"project_id","definition":"project_id","predicate":null},{"name":"recipient_type","definition":"recipient_type","predicate":null},{"name":"status","definition":"status","predicate":null}]},"notification_log_aggregations_pkey":{"name":"notification_log_aggregations_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]}}
 */
export default interface LogAggregationsTable {
  id: ColumnType<LogAggregationsId, LogAggregationsId | undefined, LogAggregationsId>;

  projectId: ColumnType<ProjectsId, ProjectsId, ProjectsId>;

  environmentId: ColumnType<EnvironmentsId, EnvironmentsId, EnvironmentsId>;

  date: ColumnType<Date, Date, Date>;

  recipientType: ColumnType<TemplateType, TemplateType, TemplateType>;

  status: ColumnType<NotificationStatus, NotificationStatus, NotificationStatus>;

  count: ColumnType<number, number | undefined, number>;

  createdAt: ColumnType<Date, Date | undefined, Date>;

  updatedAt: ColumnType<Date, Date | undefined, Date>;

  organizationId: ColumnType<OrganizationsId, OrganizationsId | undefined, OrganizationsId>;
}

export type LogAggregations = Selectable<LogAggregationsTable>;

export type NewLogAggregations = Insertable<LogAggregationsTable>;

export type LogAggregationsUpdate = Updateable<LogAggregationsTable>;

export const logAggregationsId = z.string() as unknown as z.ZodType<
  LogAggregationsId,
  LogAggregationsId
>;

export const logAggregations = z.object({
  id: z.lazy(() => logAggregationsId),
  projectId: z.lazy(() => projectsId),
  environmentId: z.lazy(() => environmentsId),
  date: z.date(),
  recipientType: templateType,
  status: notificationStatus,
  count: z.number(),
  createdAt: z.date(),
  updatedAt: z.date(),
  organizationId: z.lazy(() => organizationsId),
});

export const logAggregationsInitializer = z.object({
  id: z.lazy(() => logAggregationsId).optional(),
  projectId: z.lazy(() => projectsId),
  environmentId: z.lazy(() => environmentsId),
  date: z.date(),
  recipientType: templateType,
  status: notificationStatus,
  count: z.number().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  organizationId: z.lazy(() => organizationsId).optional(),
});

export const logAggregationsMutator = z.object({
  id: z.lazy(() => logAggregationsId).optional(),
  projectId: z.lazy(() => projectsId).optional(),
  environmentId: z.lazy(() => environmentsId).optional(),
  date: z.date().optional(),
  recipientType: templateType.optional(),
  status: notificationStatus.optional(),
  count: z.number().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  organizationId: z.lazy(() => organizationsId).optional(),
});

// ── constraints for LogAggregations ─────────────────────────────────
export const LogAggregationsConstraints = {
  log_aggregations_date_idx: {
    name: "log_aggregations_date_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "date", definition: "date", predicate: null }],
  },
  log_aggregations_environment_id_idx: {
    name: "log_aggregations_environment_id_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "environment_id", definition: "environment_id", predicate: null }],
  },
  log_aggregations_project_id_idx: {
    name: "log_aggregations_project_id_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "project_id", definition: "project_id", predicate: null }],
  },
  log_aggregations_recipient_type_idx: {
    name: "log_aggregations_recipient_type_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "recipient_type", definition: "recipient_type", predicate: null }],
  },
  log_aggregations_status_idx: {
    name: "log_aggregations_status_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "status", definition: "status", predicate: null }],
  },
  log_aggregations_unique_idx: {
    name: "log_aggregations_unique_idx",
    isPrimary: false,
    isUnique: true,
    columns: [
      { name: "date", definition: "date", predicate: null },
      { name: "environment_id", definition: "environment_id", predicate: null },
      { name: "project_id", definition: "project_id", predicate: null },
      { name: "recipient_type", definition: "recipient_type", predicate: null },
      { name: "status", definition: "status", predicate: null },
    ],
  },
  notification_log_aggregations_pkey: {
    name: "notification_log_aggregations_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
} as const;
export type LogAggregationsConstraintsType = typeof LogAggregationsConstraints;
